import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { criaPulseSentimento, verificaPulseSentimentoSemana } from 'helpers/api/pulseSentimento';
import { resetFlagOnNewLogin } from 'helpers/resetFlagOnNewLogin';
import { useSelector } from 'react-redux';

const limitHourInWeek = 12 // 12

export function useWeeklyPulse() {
  const { user } = useSelector((state: any) => ({
    user: state.Auth.user,
  }));

  const [status, setStatus] = useState<'loading' | 'open' | 'button' | 'hidden'>('loading');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const colaborador_id = user?.usuario?.colaborador?.id
  const token = user?.token

  // Função para atualizar o status inicial com base em answered e data
  useEffect(() => {
    let hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let showTimeoutId: ReturnType<typeof setTimeout> | null = null;

    resetFlagOnNewLogin('pulse', 'pulseClosed', token);

    async function fetchPulseStatus() {
      try {
        const response = await verificaPulseSentimentoSemana(colaborador_id)
        const pulseSentimentoSemana = response.data
        
        const answered = !!pulseSentimentoSemana
        setIsLoading(false);

        const now = dayjs();
        const day = now.day();      // 0 (Dom) ... 6 (Sab):contentReference[oaicite:2]{index=2}
        const hour = now.hour();    // 0 a 23 (hora do dia):contentReference[oaicite:3]{index=3}

        // Função utilitária para programar mudança de status no futuro
        const scheduleTimeouts = () => {
          // Tempo até próxima quinta 12:00
          let nextThurNoon = now.day(4).hour(limitHourInWeek).minute(0).second(0); 
          if (nextThurNoon.isBefore(now)) {
            nextThurNoon = nextThurNoon.add(1, 'week');
          }
          const msToThurNoon = nextThurNoon.diff(now);
          hideTimeoutId = setTimeout(() => {
            sessionStorage.removeItem('pulseClosed');
            setStatus('hidden');
          }, msToThurNoon);

          // Tempo até próxima sexta-feira 00:00 (início do próximo ciclo)
          let nextFriMidnight = now.day(5).startOf('day');
          if (nextFriMidnight.isBefore(now) || nextFriMidnight.isSame(now)) {
            nextFriMidnight = nextFriMidnight.add(1, 'week');
          }
          const msToFri = nextFriMidnight.diff(now);
          showTimeoutId = setTimeout(() => {
            // Reset para novo ciclo: removemos flag e rebuscamos status
            sessionStorage.removeItem('pulseClosed');
            fetchPulseStatus();
          }, msToFri);
        };

        // Se já respondeu, fica oculto e agendamos próximo ciclo
        if (answered) {
          setStatus('hidden');
          scheduleTimeouts();

        } else {
          // Verifica se estamos entre sexta (day=5) e quinta 12h (day=4, hour<12)
          const inPeriod =
            (day === 5) ||
            (day === 6) ||
            (day === 0) ||
            (day === 1) ||
            (day === 2) ||
            (day === 3) ||
            (day === 4 && hour < limitHourInWeek);

          if (inPeriod) {
            // Dentro do período: se já fechou antes, mostra botão; senão, abre modal
            const closed = sessionStorage.getItem('pulseClosed');
            setStatus(closed ? 'button' : 'open');
            scheduleTimeouts();
          } else {
            // Fora do período (após quinta 12h): oculta e aguarda próxima sexta
            setStatus('hidden');
            scheduleTimeouts();
          }
        }
      } catch (error) {
        console.error('Erro ao buscar pulse atual:', error);
        setIsLoading(false);
        setStatus('hidden');
      }
    }

    if (colaborador_id) {
      fetchPulseStatus();
    }

    return () => {
      // Limpa timeouts ao desmontar
      if (hideTimeoutId) clearTimeout(hideTimeoutId);
      if (showTimeoutId) clearTimeout(showTimeoutId);
    };
  }, [colaborador_id, token]);

  // Fecha temporariamente o modal (chamado ao usuário fechar o modal)
  function closeModal() {
    sessionStorage.setItem('pulseClosed', 'true');
    setStatus('button');
  }

  // Fecha temporariamente o modal (chamado ao usuário fechar o modal)
  function openModal() {
    sessionStorage.setItem('pulseClosed', 'open');
    setStatus('open');
  }

  // Responde ao pulse e atualiza status na API
  async function answerPulse(data: any) {
    setIsSubmitting(true);
    try {
      await criaPulseSentimento(data)
      sessionStorage.removeItem('pulseClosed');
      setStatus('hidden');
    } catch (error) {
      throw error
    } finally {
      setIsSubmitting(false);
    }
  }

  return { status, closeModal, openModal, answerPulse, isLoading, isSubmitting };
}
